import { CircleHelpIcon } from "lucide-react";
import React from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

import { Checkbox } from "~/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";

interface CheckboxOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
  readonly tooltip?: string;
}

interface CheckboxGroupFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  options: readonly CheckboxOption[];
  exclusiveOptions?: readonly string[];
  maxSelections?: number;
  onTrigger?: { [key: string]: () => void };
}

/**
 * Standard react-hook-form Controller-based checkbox group
 * This is the simple, proven approach recommended by react-hook-form docs
 */
export function CheckboxGroupField<T extends FieldValues>({
  name,
  control,
  options,
  exclusiveOptions = [],
  maxSelections,
  onTrigger = {},
}: CheckboxGroupFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[] as T[FieldPath<T>]}
      render={({ field }) => {
        const values = (field.value as string[]) || [];

        const handleChange = (optionValue: string, checked: boolean) => {
          let newValues: string[];

          if (checked) {
            if (exclusiveOptions.includes(optionValue)) {
              // Exclusive option - clear all others (bypasses maxSelections)
              newValues = [optionValue];
            } else {
              // Check max selections for regular options
              if (maxSelections && values.length >= maxSelections) {
                return;
              }
              // Regular option - add to list, remove exclusive options
              const filtered = values.filter(
                (v) => !exclusiveOptions.includes(v),
              );
              newValues = [...filtered, optionValue];
            }

            // Trigger any actions
            if (onTrigger[optionValue]) {
              onTrigger[optionValue]();
            }
          } else {
            // Remove from array
            newValues = values.filter((v) => v !== optionValue);
          }

          field.onChange(newValues);
        };

        return (
          <div className="flex flex-col gap-y-4">
            {options.map(({ value, label, description, tooltip }) => (
              <div key={value} className="flex items-start gap-2">
                <Checkbox
                  id={`${name}-${value}`}
                  checked={values.includes(value)}
                  disabled={
                    !!maxSelections &&
                    !values.includes(value) &&
                    values.length >= maxSelections &&
                    !exclusiveOptions.includes(value)
                  }
                  onCheckedChange={(checked) => handleChange(value, !!checked)}
                >
                  <span className="inline-flex items-center gap-1">
                    {label}
                    {tooltip && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CircleHelpIcon className="size-3.5 cursor-help text-gray-400 hover:text-gray-600" />
                        </TooltipTrigger>
                        <TooltipContent>{tooltip}</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                  {description && (
                    <p className="text-text-tertiary text-sm font-normal">
                      {description}
                    </p>
                  )}
                </Checkbox>
              </div>
            ))}
          </div>
        );
      }}
    />
  );
}
